import { ClearOutlined } from '@ant-design/icons';
import { IDEA_STATUSES, IDEA_STATUS_LABELS, IDEA_STATUS_VALUES, REFERENCE_TYPES } from '@cult/domain';
import { IDEA_SORTS, type IdeaOrder, type IdeaSort, type IdeaStatus, SORT_ORDERS } from '@cult/schemas';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Input, Segmented, Select, Skeleton } from 'antd';
import { useState } from 'react';
import {
  BlankState,
  CountBadge,
  type CreatedRange,
  CreatedRangeFilter,
  ExportCsvButton,
  FilterRow,
  FilterRowSpacer,
  InlineError,
  PageHeader,
  createdRangeParams,
  hasCreatedRange,
  useTablePagination,
} from '../../../../components';

import { AdminIdeaDrawer, AdminIdeasTable } from '../../../../features/admin/ideas/components';
import { type IdeaFilters, buildIdeaParams, useIdeasCount, useIdeasPaged } from '../../../../features/ideas';
import { useReferenceItems } from '../../../../features/reference';
import { UserPicker } from '../../../../features/users';
import { recordNumberParam } from '../../../../lib/search-params';

const STATUS_OPTIONS: Array<{ value: IdeaStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  ...IDEA_STATUS_VALUES.map((status) => ({ value: status, label: IDEA_STATUS_LABELS[status] })),
];

type Search = {
  ideaId?: string;
};

const IdeasPage = () => {
  const { ideaId } = Route.useSearch();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<IdeaStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [authorFilter, setAuthorFilter] = useState<string>('');
  const [titleFilter, setTitleFilter] = useState<string>('');
  const [titleInput, setTitleInput] = useState<string>('');
  const [dateRange, setDateRange] = useState<CreatedRange>(null);
  const [sort, setSort] = useState<IdeaSort>(IDEA_SORTS.NEW);
  const [order, setOrder] = useState<IdeaOrder>(SORT_ORDERS.DESC);
  const filters: IdeaFilters = { sort, order, adminView: true, ...createdRangeParams(dateRange) };
  if (statusFilter) filters.status = statusFilter;
  if (categoryFilter) filters.category = categoryFilter;
  if (authorFilter) filters.author = authorFilter;
  if (titleFilter) filters.q = titleFilter;

  const { page, pageSize, buildPagination } = useTablePagination(JSON.stringify(filters));
  const ideasQuery = useIdeasPaged(filters, page, pageSize);
  const pendingCountQuery = useIdeasCount({ adminView: true, status: IDEA_STATUSES.KIV });
  const pendingCount = pendingCountQuery.data?.count ?? 0;
  const categoriesQuery = useReferenceItems(REFERENCE_TYPES.CATEGORY);
  const all = ideasQuery.data?.ideas ?? [];
  const hasActiveFilters = Boolean(
    statusFilter || categoryFilter || authorFilter || titleFilter || hasCreatedRange(dateRange),
  );

  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setAuthorFilter('');
    setTitleFilter('');
    setTitleInput('');
    setDateRange(null);
  };

  const applyTitle = (v: string) => {
    setTitleInput(v);
    setTitleFilter(v.trim());
  };

  const openIdea = (id: string) => {
    navigate({ to: '/admin/ideas', search: (prev: Search) => ({ ...prev, ideaId: id }) });
  };

  const closeIdea = () => {
    navigate({ to: '/admin/ideas', search: (prev: Search) => ({ ...prev, ideaId: undefined }) });
  };

  const handleSortChange = (nextSort: IdeaSort, nextOrder: IdeaOrder) => {
    setSort(nextSort);
    setOrder(nextOrder);
  };

  return (
    <Flex vertical gap="large">
      <PageHeader
        title="Ideas"
        subtitle="Moderation queue. Review KIV ideas and manage their lifecycle."
        action={<ExportCsvButton path="/ideas/export" params={buildIdeaParams(filters)} fileBaseName="ideas" />}
      />

      <Segmented
        value={statusFilter === IDEA_STATUSES.KIV ? IDEA_STATUSES.KIV : ''}
        onChange={(v) => setStatusFilter((v as string) === IDEA_STATUSES.KIV ? IDEA_STATUSES.KIV : '')}
        options={[
          { value: '', label: 'All ideas' },
          {
            value: IDEA_STATUSES.KIV,
            label: (
              <span>
                KIV <CountBadge count={pendingCount} />
              </span>
            ),
          },
        ]}
      />

      <FilterRow>
        <Input.Search
          placeholder="Search title"
          value={titleInput}
          onChange={(e) => setTitleInput(e.target.value)}
          onSearch={applyTitle}
          allowClear
          style={{ width: 240 }}
        />
        <Select<IdeaStatus | ''>
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_OPTIONS}
          style={{ width: 180 }}
        />
        <Select<string>
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[
            { value: '', label: 'All categories' },
            ...(categoriesQuery.data?.map((c) => ({ value: c.slug, label: c.name })) ?? []),
          ]}
          style={{ width: 200 }}
          loading={categoriesQuery.isPending}
        />
        <UserPicker
          value={authorFilter}
          onChange={(v) => setAuthorFilter(v ?? '')}
          allowClear
          includeInactive
          placeholder="Filter by author"
          style={{ width: 200 }}
        />
        <CreatedRangeFilter value={dateRange} onChange={setDateRange} />
        <FilterRowSpacer />
        {hasActiveFilters ? (
          <Button type="text" size="small" icon={<ClearOutlined />} onClick={clearFilters}>
            Reset filters
          </Button>
        ) : null}
      </FilterRow>

      {ideasQuery.isError ? <InlineError title="Couldn't load ideas" detail={ideasQuery.error.message} /> : null}

      {ideasQuery.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : all.length === 0 && !ideasQuery.isError ? (
        <BlankState
          title={hasActiveFilters ? 'No ideas match these filters' : 'No ideas yet'}
          hint={
            hasActiveFilters
              ? 'Try a different status or category — or clear the filters below.'
              : 'When members submit ideas they will land in this queue.'
          }
          action={
            hasActiveFilters ? (
              <Button size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : all.length === 0 ? null : (
        <AdminIdeasTable
          ideas={all}
          loading={ideasQuery.isFetching}
          onOpenIdea={openIdea}
          sort={sort}
          order={order}
          onSortChange={handleSortChange}
          defaultSort={IDEA_SORTS.NEW}
          defaultOrder={SORT_ORDERS.DESC}
          pagination={buildPagination(ideasQuery.data?.total ?? 0)}
        />
      )}

      <AdminIdeaDrawer ideaId={ideaId ?? null} onClose={closeIdea} ideas={all} onNavigate={openIdea} />
    </Flex>
  );
};

export const Route = createFileRoute('/_auth/admin/ideas/')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    ideaId: recordNumberParam(search.ideaId),
  }),
  component: IdeasPage,
});
